/**
 * Search Context - Global search across all user data
 * Local smart search with NLS intent detection
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode, useMemo, useCallback } from 'react';
import { useProfile } from './ProfileContext';
import { useLocalMode } from './LocalModeContext';
import { healthService } from '../services/healthService';
import { financialService } from '../services/financialService';
import { insuranceService } from '../services/insuranceService';
import { usesGoogleDrive as checkUsesGoogleDrive, SubscriptionPlan } from '../constants/planLimits';
import { filterByMember } from '../utils/filterUtils';
import { Address, Vehicle, EmergencyContact, ServiceProvider, UtilityAccount } from '../types/profile';
import { InsurancePolicy } from '../types/insurance';
import { HealthProvider } from '../types/health';
import { FinancialInstitution } from '../types/financial';
import {
  naturalLanguageSearchService,
  SearchableEntityType,
  SearchIntent,
} from '../services/naturalLanguageSearchService';
import {
  smartMatch,
  buildSearchableText,
  detectQueryIntent,
  rankResults,
  RankedResult,
} from '../utils/smartSearch';

export type SearchResultType = 'address' | 'insurance' | 'vehicle' | 'emergency-contact' | 'healthcare' | 'finance' | 'service-provider' | 'utility' | 'reminder';

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  description?: string;
  relevanceScore?: number;
  data: Address | InsurancePolicy | Vehicle | EmergencyContact | HealthProvider | FinancialInstitution | ServiceProvider | UtilityAccount;
}

// Empty state with suggestions
export interface SearchEmptyState {
  message: string;
  suggestions?: string[];
  alternativeQueries?: string[];
  addPrompt?: {
    entityType: SearchableEntityType;
    entitySubtype?: string;
    message: string;
  };
}

interface SearchContextType {
  query: string;
  setQuery: (query: string) => void;
  results: SearchResult[];
  isSearching: boolean;
  memberFilter: string; // 'all' for family/all members, or specific userId
  setMemberFilter: (filter: string) => void;
  // NLS enhancements
  searchIntent: SearchIntent | null;
  emptyState: SearchEmptyState | null;
  suggestions: string[];
  recordCorrection: (intendedType: SearchableEntityType, subtype?: string) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [query, setQuery] = useState('');
  // Debounced query — prevents recomputing search on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(debounceRef.current);
  }, [query]);
  const [memberFilter, setMemberFilter] = useState<string>('all'); // 'all' = Family (all members)
  const { currentProfile, pilotInfo, isLoading: profileLoading } = useProfile();
  const { isLocalMode, isPrivateBrowsingMode, isLoading: localModeLoading } = useLocalMode();

  // State for data from separate Firestore collections
  const [healthProviders, setHealthProviders] = useState<HealthProvider[]>([]);
  const [financialInstitutions, setFinancialInstitutions] = useState<FinancialInstitution[]>([]);
  const [insurancePolicies, setInsurancePolicies] = useState<InsurancePolicy[]>([]);

  // Subscribe to real-time data from separate collections
  // Skip Firestore subscriptions for Pro users (Google Drive storage) - their data is in Google Drive
  // Skip Firestore subscriptions in Local/Guest Mode - data is stored locally
  // CRITICAL: Wait for profile to load before checking usesGoogleDrive to prevent race condition
  useEffect(() => {
    // Wait for LocalModeContext to finish loading before making any decisions
    if (localModeLoading) return;

    // Skip Firestore subscriptions in Local/Guest Mode or Private Browsing - data is stored locally
    if (isLocalMode || isPrivateBrowsingMode) {
      setHealthProviders([]);
      setFinancialInstitutions([]);
      setInsurancePolicies([]);
      return;
    }

    // Wait for profile to finish loading before deciding on storage type
    // This prevents the race condition where pilotInfo is initially null
    // and usesGoogleDrive defaults to false, causing Firestore subscriptions to start
    if (profileLoading) return;

    if (!currentProfile?.id) return;

    // CRITICAL: Also wait for pilotInfo to be available before making storage decisions
    // This prevents Firestore permission errors during initial signup when pilotInfo hasn't loaded yet
    if (!pilotInfo) return;

    // Check if user uses Google Drive storage (Pro plan)
    const userPlan = (pilotInfo.membershipPlan || 'free') as SubscriptionPlan;
    const usesGoogleDrive = checkUsesGoogleDrive(userPlan);

    // For Pro users, data comes from Google Drive, not Firestore collections
    if (usesGoogleDrive) {
      // Clear any stale Firestore data
      setHealthProviders([]);
      setFinancialInstitutions([]);
      setInsurancePolicies([]);
      return;
    }

    const unsubHealth = healthService.subscribeToProviders(currentProfile.id, setHealthProviders);
    const unsubFinancial = financialService.subscribeToInstitutions(currentProfile.id, setFinancialInstitutions);
    const unsubInsurance = insuranceService.subscribeToPolicies(currentProfile.id, setInsurancePolicies);

    return () => {
      unsubHealth();
      unsubFinancial();
      unsubInsurance();
    };
  }, [currentProfile?.id, pilotInfo, profileLoading, isLocalMode, isPrivateBrowsingMode, localModeLoading]);

  // Get current user ID for member filtering

  // Collect all service providers from addresses (unfiltered - for empty state check)
  const allServiceProviders = useMemo(() => {
    if (!currentProfile) return [];
    const providers: ServiceProvider[] = [];
    currentProfile.addresses.forEach(addr => {
      if (addr.serviceProviders) {
        providers.push(...addr.serviceProviders);
      }
    });
    return providers;
  }, [currentProfile]);

  // Collect all utility accounts from addresses (unfiltered - for empty state check)
  const allUtilityAccounts = useMemo(() => {
    if (!currentProfile) return [];
    const utilities: UtilityAccount[] = [];
    currentProfile.addresses.forEach(addr => {
      if (addr.utilityAccounts) {
        utilities.push(...addr.utilityAccounts);
      }
    });
    return utilities;
  }, [currentProfile]);

  // Collect service providers from member-filtered addresses
  const filteredServiceProviders = useMemo(() => {
    if (!currentProfile) return [];
    const filteredAddresses = filterByMember(currentProfile.addresses, memberFilter);
    const providers: ServiceProvider[] = [];
    filteredAddresses.forEach(addr => {
      if (addr.serviceProviders) {
        providers.push(...addr.serviceProviders);
      }
    });
    return providers;
  }, [currentProfile, memberFilter]);

  // Collect utility accounts from member-filtered addresses
  const filteredUtilityAccounts = useMemo(() => {
    if (!currentProfile) return [];
    const filteredAddresses = filterByMember(currentProfile.addresses, memberFilter);
    const utilities: UtilityAccount[] = [];
    filteredAddresses.forEach(addr => {
      if (addr.utilityAccounts) {
        utilities.push(...addr.utilityAccounts);
      }
    });
    return utilities;
  }, [currentProfile, memberFilter]);

  // Parse search intent using NLS (debounced)
  const searchIntent = useMemo(() => {
    if (!debouncedQuery.trim()) return null;
    return naturalLanguageSearchService.parseIntent(debouncedQuery);
  }, [debouncedQuery]);

  // Get search suggestions (debounced)
  const suggestions = useMemo(() => {
    if (!debouncedQuery.trim() || debouncedQuery.length < 2) return [];
    return naturalLanguageSearchService.getSuggestions(debouncedQuery);
  }, [debouncedQuery]);

  // Main search results with smart search (debounced)
  const { results, emptyState } = useMemo(() => {
    if (!debouncedQuery.trim() || !currentProfile) {
      return { results: [], emptyState: null };
    }

    // Use smart search to tokenize and detect intent
    const queryIntent = detectQueryIntent(debouncedQuery);
    const searchTerms = queryIntent.searchTerms;

    // If no meaningful search terms, return empty
    if (searchTerms.length === 0) {
      return { results: [], emptyState: null };
    }

    const rankedResults: RankedResult<SearchResult>[] = [];

    // Use NLS intent for entity filtering (optional enhancement)
    const nlsIntent = naturalLanguageSearchService.parseIntent(query);
    const targetEntityType = nlsIntent.entityType || queryIntent.entityHint;

    // Helper to add a result with smart matching
    const tryAddResult = (
      searchableText: string,
      result: SearchResult,
      boostIfEntityMatch: boolean = false
    ) => {
      const match = smartMatch(searchableText, searchTerms);
      if (match) {
        let score = match.score;
        // Boost score if entity type matches the detected intent
        if (boostIfEntityMatch && targetEntityType === result.type) {
          score = Math.min(1, score + 0.2);
        }
        rankedResults.push({
          item: { ...result, relevanceScore: score },
          score,
          matchedTerms: match.matchedTerms,
          matchType: match.matchType,
        });
      }
    };

    // Search service providers (member-filtered)
    if (!targetEntityType || targetEntityType === 'service-provider') {
      filteredServiceProviders.forEach((provider) => {
        const category = provider.customCategory || provider.category;
        const searchableText = buildSearchableText(
          [provider.name, category, provider.notes, provider.tags?.join(' ')],
          ['service', 'provider', 'contractor']
        );
        tryAddResult(searchableText, {
          id: provider.id,
          type: 'service-provider',
          title: provider.name,
          subtitle: category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' '),
          description: provider.phoneNumber,
          data: provider,
        }, true);
      });
    }

    // Search utility accounts (member-filtered)
    if (!targetEntityType || targetEntityType === 'utility') {
      filteredUtilityAccounts.forEach((utility) => {
        const utilityType = utility.customType || utility.type;
        const searchableText = buildSearchableText(
          [utility.provider, utilityType, utility.notes],
          ['utility', 'bill', 'account']
        );
        tryAddResult(searchableText, {
          id: utility.id,
          type: 'utility',
          title: utility.provider,
          subtitle: utilityType.charAt(0).toUpperCase() + utilityType.slice(1),
          description: utility.accountNumber ? `Account: ****${utility.accountNumber.slice(-4)}` : '',
          data: utility,
        }, true);
      });
    }

    // Search addresses
    if (!targetEntityType || targetEntityType === 'address') {
      const filteredAddresses = filterByMember(currentProfile.addresses, memberFilter);
      filteredAddresses.forEach((address) => {
        const searchableText = buildSearchableText(
          [address.label, address.street, address.city, address.state, address.zipCode, address.type],
          ['address', 'home', 'house', 'location', 'property']
        );
        tryAddResult(searchableText, {
          id: address.id,
          type: 'address',
          title: address.label || `${address.type} Address`,
          subtitle: address.street,
          description: `${address.city}, ${address.state} ${address.zipCode}`,
          data: address,
        }, true);
      });
    }

    // Search insurance policies
    if (!targetEntityType || targetEntityType === 'insurance') {
      const filteredInsurance = filterByMember(insurancePolicies, memberFilter);
      filteredInsurance.forEach((policy) => {
        const insuranceType = policy.insuranceType || 'other';
        const searchableText = buildSearchableText(
          [insuranceType, policy.company, policy.policyNumber, policy.notes],
          ['insurance', 'policy', 'coverage', 'premium']
        );
        tryAddResult(searchableText, {
          id: policy.id,
          type: 'insurance',
          title: `${insuranceType.charAt(0).toUpperCase() + insuranceType.slice(1)} Insurance`,
          subtitle: policy.company,
          description: policy.policyNumber ? `Policy: ****${policy.policyNumber.slice(-4)}` : policy.company,
          data: policy,
        }, true);
      });
    }

    // Search vehicles - IMPORTANT: include VIN and related keywords
    if (!targetEntityType || targetEntityType === 'vehicle') {
      const filteredVehicles = filterByMember(currentProfile.vehicles, memberFilter);
      filteredVehicles.forEach((vehicle) => {
        const searchableText = buildSearchableText(
          [vehicle.make, vehicle.model, String(vehicle.year), vehicle.vin, vehicle.licensePlate, vehicle.color, vehicle.type],
          ['vehicle', 'car', 'auto', 'automobile', 'vin', 'license', 'plate', 'registration']
        );
        const queryLower = debouncedQuery.toLowerCase();
        const showVin = queryLower.includes('vin') && vehicle.vin;
        const description = showVin
          ? `VIN: ${vehicle.vin}`
          : (vehicle.color || vehicle.type || '');
        tryAddResult(searchableText, {
          id: vehicle.id,
          type: 'vehicle',
          title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          subtitle: vehicle.licensePlate || (showVin ? '' : vehicle.vin) || '',
          description,
          data: vehicle,
        }, true);
      });
    }

    // Search emergency contacts
    if (!targetEntityType || targetEntityType === 'emergency-contact') {
      currentProfile.emergencyContacts?.forEach((contact) => {
        const searchableText = buildSearchableText(
          [contact.name, contact.relationship, contact.phoneNumber, contact.email],
          ['emergency', 'contact', 'phone']
        );
        tryAddResult(searchableText, {
          id: contact.id,
          type: 'emergency-contact',
          title: contact.name,
          subtitle: contact.relationship,
          description: contact.phoneNumber,
          data: contact,
        }, true);
      });
    }

    // Search healthcare providers
    if (!targetEntityType || targetEntityType === 'healthcare') {
      const filteredHealthProviders = filterByMember(healthProviders, memberFilter);
      filteredHealthProviders.forEach((provider) => {
        const addressString = provider.address ? `${provider.address.line1} ${provider.address.city} ${provider.address.state}` : '';
        const searchableText = buildSearchableText(
          [provider.name, provider.specialty, provider.phone, addressString, provider.notes],
          ['healthcare', 'health', 'doctor', 'physician', 'medical', 'provider', 'clinic']
        );
        tryAddResult(searchableText, {
          id: provider.id,
          type: 'healthcare',
          title: provider.name,
          subtitle: provider.specialty || 'Healthcare Provider',
          description: provider.phone || addressString || '',
          data: provider,
        }, true);
      });
    }

    // Search financial institutions
    if (!targetEntityType || targetEntityType === 'finance') {
      const filteredFinancialInstitutions = filterByMember(financialInstitutions, memberFilter);
      filteredFinancialInstitutions.forEach((institution) => {
        const primaryAccount = institution.accounts?.[0];
        const accountNumber = primaryAccount?.accountNumber || '';
        const searchableText = buildSearchableText(
          [institution.name, institution.type, accountNumber, institution.notes],
          ['financial', 'finance', 'bank', 'credit', 'account', 'money']
        );
        tryAddResult(searchableText, {
          id: institution.id,
          type: 'finance',
          title: institution.name,
          subtitle: institution.type || 'Financial Institution',
          description: accountNumber ? `Account: ****${accountNumber.slice(-4)}` : '',
          data: institution,
        }, true);
      });
    }

    // Rank and sort results
    const ranked = rankResults(rankedResults, queryIntent);
    const searchResults = ranked.map(r => r.item);

    // Build empty state if no results
    let emptyStateResult: SearchEmptyState | null = null;
    if (searchResults.length === 0) {
      emptyStateResult = buildEmptyState(nlsIntent, {
        hasServiceProviders: allServiceProviders.length > 0,
        hasHealthProviders: healthProviders.length > 0,
        hasInsurance: insurancePolicies.length > 0,
        hasAddresses: (currentProfile.addresses?.length || 0) > 0,
        hasVehicles: (currentProfile.vehicles?.length || 0) > 0,
      });
    }

    return { results: searchResults, emptyState: emptyStateResult };
  }, [debouncedQuery, currentProfile, healthProviders, financialInstitutions, insurancePolicies, memberFilter, allServiceProviders, allUtilityAccounts, filteredServiceProviders, filteredUtilityAccounts]);

  // Record correction for NLS learning
  const recordCorrection = useCallback((intendedType: SearchableEntityType, subtype?: string) => {
    if (query.trim()) {
      naturalLanguageSearchService.recordCorrection(query, intendedType, subtype);
    }
  }, [query]);

  const isSearching = query.trim().length > 0;

  return (
    <SearchContext.Provider value={{
      query,
      setQuery,
      results,
      isSearching,
      memberFilter,
      setMemberFilter,
      searchIntent,
      emptyState,
      suggestions,
      recordCorrection,
    }}>
      {children}
    </SearchContext.Provider>
  );
}

/**
 * Build helpful empty state message based on search intent
 */
function buildEmptyState(
  intent: SearchIntent,
  dataAvailability: {
    hasServiceProviders: boolean;
    hasHealthProviders: boolean;
    hasInsurance: boolean;
    hasAddresses: boolean;
    hasVehicles: boolean;
  }
): SearchEmptyState {
  const { entityType, entitySubtype, normalizedQuery } = intent;

  if (entityType === 'service-provider' && entitySubtype) {
    const categoryLabel = entitySubtype.replace('_', ' ');
    return {
      message: `You haven't added any ${categoryLabel}s yet`,
      suggestions: ['You can add service providers from your address page'],
      addPrompt: {
        entityType: 'service-provider',
        entitySubtype,
        message: `Add a ${categoryLabel}`
      }
    };
  }

  if (entityType === 'healthcare' && entitySubtype) {
    return {
      message: `You haven't added any ${entitySubtype}s yet`,
      suggestions: dataAvailability.hasHealthProviders
        ? ['Try browsing all healthcare providers']
        : ['Add healthcare providers from the Healthcare page'],
      addPrompt: {
        entityType: 'healthcare',
        entitySubtype,
        message: `Add a ${entitySubtype}`
      }
    };
  }

  if (entityType === 'insurance' && entitySubtype) {
    return {
      message: `You haven't added ${entitySubtype} insurance yet`,
      suggestions: dataAvailability.hasInsurance
        ? ['Try searching for other insurance types']
        : ['Add insurance policies from the Insurance page'],
      addPrompt: {
        entityType: 'insurance',
        entitySubtype,
        message: `Add ${entitySubtype} insurance`
      }
    };
  }

  if (entityType === 'address') {
    return {
      message: dataAvailability.hasAddresses
        ? `No addresses match "${normalizedQuery}"`
        : "You haven't added any addresses yet",
      suggestions: dataAvailability.hasAddresses
        ? ['Try using different keywords']
        : ['Add an address to get started'],
      addPrompt: !dataAvailability.hasAddresses ? {
        entityType: 'address',
        message: 'Add an address'
      } : undefined
    };
  }

  if (entityType === 'vehicle') {
    return {
      message: dataAvailability.hasVehicles
        ? `No vehicles match "${normalizedQuery}"`
        : "You haven't added any vehicles yet",
      suggestions: dataAvailability.hasVehicles
        ? ['Try searching by make, model, or year']
        : ['Add a vehicle to get started'],
      addPrompt: !dataAvailability.hasVehicles ? {
        entityType: 'vehicle',
        message: 'Add a vehicle'
      } : undefined
    };
  }

  return {
    message: `No results found for "${normalizedQuery}"`,
    suggestions: ['Try using different keywords', 'Check your spelling'],
    alternativeQueries: ['show addresses', 'find plumber', 'my insurance']
  };
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
